/**
 * Rich SEO Content Blocks
 * =======================
 * Premium, hand-built building blocks used by the bespoke top-traffic SEO pages.
 * These are richer/more visual than the generic ContentBlocks and are meant for
 * pages we actively invest in for SEO / GEO / AEO.
 */

import React, { useState } from 'react';
import SEOPageHero from '@/components/seo/rich/SEOPageHero';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Check, X, ChevronDown, Zap, ShieldCheck, TrendingDown,
  Clock, Star, Info, MapPin, Calendar, BatteryCharging, Gauge
} from 'lucide-react';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

// ── Brand palette helpers ───────────────────────────────────────────────
export const INK = '#1F2E33';
export const PINE = '#2F424D';
export const GOLD = '#D4A62A';
export const CREAM = '#F6F3EC';

// ── Hero ────────────────────────────────────────────────────────────────
export function RichHero(props) {
  return <SEOPageHero {...props} />;
}

// ── Table of contents (jump links) ──────────────────────────────────────
export function TableOfContents({ items = [] }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Indhold" className="bg-[#F6F3EC] border-y border-[#263A43]/10">
      <div className="container mx-auto px-6 py-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 max-w-4xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-wide text-[#5E7B8C]">På denne side</span>
          {items.map((it, i) => (
            <a key={i} href={`#${it.id}`} className="text-sm font-medium text-[#2F424D] hover:text-[#D4A62A] transition-colors">
              {it.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

// ── Prose section wrapper ───────────────────────────────────────────────
export function ProseSection({ id, heading, kicker, children, alt = false, narrow = true }) {
  return (
    <section id={id} className={`py-12 md:py-16 ${alt ? 'bg-[#F6F3EC]' : 'bg-white'}`}>
      <div className="container mx-auto px-6">
        <div className={narrow ? 'max-w-3xl mx-auto' : 'max-w-5xl mx-auto'}>
          {kicker && <p className="text-xs font-bold uppercase tracking-wide text-[#D4A62A] mb-2">{kicker}</p>}
          {heading && <h2 className="text-2xl md:text-3xl font-bold mb-5 text-[#1F2E33]" style={serif}>{heading}</h2>}
          <div className="seo-prose leading-relaxed space-y-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

// ── Answer box (AEO-friendly direct answer) ─────────────────────────────
export function AnswerBox({ question, children }) {
  return (
    <div className="seo-answer border-l-4 rounded-r-2xl p-5 md:p-6 my-2">
      <div className="flex items-center gap-2 mb-2">
        <Info className="w-5 h-5 text-[#0f5c4a]" />
        <p className="font-bold text-[#0f5c4a] text-sm uppercase tracking-wide">Kort svar</p>
      </div>
      {question && <p className="font-semibold text-gray-900 mb-2">{question}</p>}
      <div className="text-gray-800 leading-relaxed">{children}</div>
    </div>
  );
}

// ── Stat / fact grid ────────────────────────────────────────────────────
export function FactGrid({ facts = [], columns = 3 }) {
  const cols = columns === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3';
  return (
    <div className={`grid ${cols} gap-4`}>
      {facts.map((f, i) => (
        <div key={i} className="bg-white rounded-2xl p-5 border border-[#263A43]/10 shadow-sm">
          <p className="text-2xl font-bold text-[#1F2E33]">{f.value}</p>
          <p className="text-sm font-semibold text-[#2F424D] mt-1">{f.label}</p>
          {f.sub && <p className="text-xs text-gray-500 mt-1">{f.sub}</p>}
        </div>
      ))}
    </div>
  );
}

// ── Comparison table ────────────────────────────────────────────────────
// rows: [{ label, values: [..] }], columns: [{ name, highlight }]
export function ComparisonTable({ columns = [], rows = [], caption }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#263A43]/12 shadow-sm">
      <table className="w-full text-sm bg-white">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="bg-[#1F2E33] text-white">
            <th className="text-left font-semibold px-4 py-3"></th>
            {columns.map((c, i) => (
              <th key={i} className={`px-4 py-3 font-bold text-center ${c.highlight ? 'bg-[#D4A62A] text-[#1F2E33]' : ''}`}>
                {c.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className={ri % 2 ? 'bg-[#F6F3EC]/60' : 'bg-white'}>
              <td className="px-4 py-3 font-semibold text-gray-900 align-top">{r.label}</td>
              {r.values.map((v, vi) => (
                <td key={vi} className={`px-4 py-3 text-center align-top ${columns[vi]?.highlight ? 'font-semibold text-[#1F2E33]' : 'text-gray-700'}`}>
                  {v === true ? <Check className="w-4 h-4 text-[#0f5c4a] mx-auto" /> : v === false ? <X className="w-4 h-4 text-gray-300 mx-auto" /> : v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Pros / cons ─────────────────────────────────────────────────────────
export function ProsCons({ pros = [], cons = [], prosTitle = 'Fordele', consTitle = 'Ulemper' }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="bg-[#e8f5e9] rounded-2xl p-5 border border-[#0f5c4a]/15">
        <h4 className="font-bold text-[#0f5c4a] mb-3 flex items-center gap-2"><Check className="w-5 h-5" />{prosTitle}</h4>
        <ul className="space-y-2">
          {pros.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-800"><Check className="w-4 h-4 text-[#0f5c4a] mt-0.5 flex-shrink-0" />{p}</li>
          ))}
        </ul>
      </div>
      <div className="bg-[#fdf0ef] rounded-2xl p-5 border border-[#c0392b]/15">
        <h4 className="font-bold text-[#b23b30] mb-3 flex items-center gap-2"><X className="w-5 h-5" />{consTitle}</h4>
        <ul className="space-y-2">
          {cons.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-800"><X className="w-4 h-4 text-[#b23b30] mt-0.5 flex-shrink-0" />{c}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Step list (how-to) ──────────────────────────────────────────────────
export function StepList({ steps = [] }) {
  return (
    <ol className="space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-4">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#2F424D] text-[#E9C766] font-bold flex items-center justify-center">{i + 1}</span>
          <div>
            <p className="font-semibold text-gray-900">{s.title}</p>
            {s.body && <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{s.body}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Charger card (links to product page) ────────────────────────────────
export function ChargerCard({ charger, note }) {
  if (!charger) return null;
  return (
    <Link to={`/${charger.slug}`} className="group block bg-white rounded-2xl p-5 border border-[#263A43]/12 hover:border-[#D4A62A] hover:shadow-lg transition-all">
      <div className="w-full h-40 flex items-center justify-center mb-4 img-zoom-hover">
        <img src={charger.image} alt={charger.name} className="max-h-40 object-contain" loading="lazy" />
      </div>
      <h3 className="font-bold text-gray-900">{charger.name}</h3>
      <p className="text-xs text-gray-500 mb-2">{charger.brand} · {charger.kw} kW</p>
      <div className="flex items-baseline gap-2 mb-3">
        {charger.originalPrice > charger.price && (
          <span className="line-through text-gray-400 text-sm">{charger.originalPrice.toLocaleString('da-DK')} kr.</span>
        )}
        <span className="text-xl font-bold text-[#1F2E33]">{charger.price.toLocaleString('da-DK')} kr.</span>
      </div>
      {note && <p className="text-xs text-[#0f5c4a] font-medium mb-2">{note}</p>}
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#2F424D] group-hover:text-[#D4A62A]">
        Se {charger.name} <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

// ── FAQ accordion (rich, native details) ────────────────────────────────
export function RichFAQ({ faqs = [] }) {
  if (!faqs.length) return null;
  return (
    <div className="space-y-3 max-w-3xl mx-auto">
      {faqs.map((f, i) => (
        <details key={i} className="bg-white rounded-2xl border border-[#263A43]/12 group shadow-sm">
          <summary className="px-5 py-4 cursor-pointer font-semibold flex items-center justify-between text-gray-900 list-none">
            <span itemProp="name">{f.question}</span>
            <ChevronDown className="w-5 h-5 text-[#5E7B8C] group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
          </summary>
          <div className="px-5 pb-5 text-gray-700 leading-relaxed" itemProp="text">{f.answer}</div>
        </details>
      ))}
    </div>
  );
}

// ── Related links ───────────────────────────────────────────────────────
export function RelatedLinks({ links = [] }) {
  if (!links.length) return null;
  return (
    <section className="py-12 bg-[#F6F3EC]">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-[#1F2E33]" style={serif}>Læs også</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {links.map((l, i) => (
              <Link key={i} to={l.path} className="flex items-center gap-2 bg-white rounded-xl p-4 border border-[#263A43]/12 hover:border-[#D4A62A] transition-all">
                <span className="text-sm font-medium text-gray-900">{l.label}</span>
                <ArrowRight className="w-4 h-4 ml-auto text-gray-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Final CTA ───────────────────────────────────────────────────────────
export function RichCTA({
  headline = 'Klar til at spare på din elregning?',
  subtext = 'Skift til enkel energi – det tager 5 minutter, og vi opsiger dit gamle selskab for dig.',
  primary = { path: '/elaftale', text: 'Se el-aftaler' },
  secondary = { path: '/opladere', text: 'Se ladebokse' },
}) {
  return (
    <section className="seo-final-cta py-16">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white" style={serif}>{headline}</h2>
        <p className="text-white/75 mb-8 max-w-xl mx-auto">{subtext}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={primary.path}>
            <Button size="lg" className="ee-button w-full sm:w-auto">
              {primary.text} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to={secondary.path}>
            <Button size="lg" className="ee-button-secondary w-full sm:w-auto">
              {secondary.text}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export const ICONS = { Zap, ShieldCheck, TrendingDown, Clock, Star, MapPin, Calendar, BatteryCharging, Gauge };